import { useEffect, useState } from "react";

import { Button } from "../../components/base/Button";
import Form from "../../components/base/Form/Form";
import { CustomerStatus } from "../../models/Customer/Customer";
import { FormSelect } from "../../components/base/Form/FormSelect";
import { enumToSelectOptions } from "./../../utils/enumToKeyValue";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { FormTextField } from "../../components/base/Form/FormTextField";
import { CreateCustomerFormFields } from "../../models/Customer/CreateCustomer";
import { AddressSearchCombobox } from "../../components/shared/AddressSearchCombo";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useCustomerMutations from "../../data/Customer/useCustomerMutations";
import { UseFormReturn } from "react-hook-form";

export function NewCustomer() {
  const { create } = useCustomerMutations();
  const { setInfo } = usePageNameContext();
  const [submitting, setSubmitting] = useState(false);

  const createCustomer = (data: CreateCustomerFormFields) => {
    setSubmitting(true);
    create.mutate(
      { ...data, status: parseInt(data.status as unknown as string) },
      {
        onSettled: () => {
          setSubmitting(false);
        },
      },
    );
  };

  useEffect(() => {
    setInfo({
      name: "New Customer",
      desc: "Create a new customer.",
      pageTitle: "New Customer",
    });
  }, []);

  return (
    <div className="bg-white rounded shadow p-4">
      {submitting ? (
        <div className="py-8">
          <LoadingWheel />
        </div>
      ) : (
        <Form<CreateCustomerFormFields>
          onSubmit={createCustomer}
          className="grid grid-cols-1 gap-y-4 md:w-3/4 md:grid-cols-2 md:gap-4"
        >
          {({
            register,
            setValue,
          }: UseFormReturn<CreateCustomerFormFields>) => (
            <>
              <div className="col-span-2 grid grid-cols-1 gap-y-4 md:gap-4 md:px-4 md:grid-cols-2">
                <div className="md:col-span-2 border-b">
                  <p className="text-base font-medium text-gray-600 font-stick pb-2">
                    Basic Information
                  </p>
                </div>
                <FormTextField
                  {...register("name", { required: true })}
                  placeholder="WeSkrap4U"
                  label="Company Name"
                />
                <FormTextField
                  {...register("usernamePrefix", { required: true })}
                  placeholder="weskrap4u"
                  label="Username Prefix"
                />
                <FormTextField
                  {...register("domain", { required: true })}
                  placeholder="wilma.weskrap4u.com"
                  label="Wilma Domain"
                />
                <FormSelect {...register("status")} label="Status">
                  {enumToSelectOptions(CustomerStatus).map((status) => (
                    <option value={status.value}>{status.label}</option>
                  ))}
                </FormSelect>
                <div />
              </div>
              <div className="grid grid-cols-1 gap-y-4 md:col-span-2 md:gap-4 md:px-4 md:pb-4 md:grid-cols-2">
                <div className="md:col-span-2 border-b">
                  <p className="text-base font-medium text-gray-600 font-stick pb-2">
                    Address
                  </p>
                </div>
                <AddressSearchCombobox
                  setValue={(value) => {
                    setValue("line1", value.line1);
                    setValue("line2", value.line2);
                    setValue("line3", value.line3);
                    setValue("line4", value.line4);
                    setValue("locality", value.locality);
                    setValue("town", value.town);
                    setValue("county", value.county);
                    setValue("postcode", value.postcode);
                  }}
                />
                <FormTextField {...register("line1")} label="Address Line 1" />
                <FormTextField {...register("line2")} label="Address Line 2" />
                <FormTextField {...register("line3")} label="Address Line 3" />
                <FormTextField {...register("line4")} label="Address Line 4" />
                <FormTextField {...register("locality")} label="Locality" />
                <FormTextField {...register("town")} label="Town" />
                <FormTextField {...register("county")} label="County" />
                <FormTextField {...register("postcode")} label="Postcode" />
                <div />
                <div className="col-span-2 mt-4 pt-4 grid grid-cols-4 gap-4 border-t">
                  <Button type="submit">Create Customer</Button>
                  <Button type="reset" variant="outlined">
                    Reset
                  </Button>
                </div>
              </div>
            </>
          )}
        </Form>
      )}
    </div>
  );
}
